import React, { useState, useEffect } from "react";
import {
  Breadcrumb,
  Divider,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Row,
  Col,
  Popconfirm,
  Space,
  Badge,
  Select,
} from "antd";
import * as Icon from "react-feather";
import { useDispatch } from "react-redux";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import CustomTable from "../../components/table/custom-table";
import { useClientCreatedSort, withCreatedColumn } from '../../shared/functional/created-sort';
import AddEditProgramDialog from "./AddEditProgramDialog";
import programService from "../../services/program-service";
import { DEGREE_LEVEL_OPTIONS } from "../../constants/app-constants";

const { Panel } = Collapse;
const { Option } = Select;

const defaultCondition = {
  q: null,
  level: null,
  faculty: null,
};

const Program = () => {
  const dispatch = useDispatch();
  const [isShowAddFileDialog, setIsShowAddFileDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [condition, setCondition] = useState(defaultCondition);
  const [dataList, setDataList] = useState([]);
  const [facultyOptions, setFacultyOptions] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowPerPage: 10,
    rowCount: 0,
  });

  useEffect(() => {
    doSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDegreeLevelLabel = (value) => {
    const found = DEGREE_LEVEL_OPTIONS.find((item) => item.value === value);
    return found ? found.label : value;
  };

  // The list endpoint returns the full array (no server-side paging contract),
  // so paging is done client-side over the fetched result.
  const mergeFacultyOptions = (list) => {
    const map = {};
    facultyOptions.forEach((item) => {
      map[item.value] = item.label;
    });
    (list || []).forEach((item) => {
      if (item && item.faculty) {
        map[item.faculty] =
          item.facultyName && item.facultyName.titleTH
            ? item.facultyName.titleTH
            : item.faculty;
      }
    });
    setFacultyOptions(
      Object.keys(map).map((key) => ({ value: key, label: map[key] }))
    );
  };

  /**
   * @param currentPage page to land on, 1 to start over.
   * @param overrides   NMU-529: search with these filters instead of the ones in
   *   state. setCondition only takes effect on the next render, so a caller that
   *   resets the filters and searches in the same handler would otherwise re-run
   *   the search it just cleared.
   */
  const doSearch = async (currentPage, overrides) => {
    try {
      dispatch(appLoaderAction.enable());

      const activeCondition = overrides ? overrides.condition : condition;

      const list = await programService.search({
        q: activeCondition.q,
        level: activeCondition.level,
        faculty: activeCondition.faculty,
      });

      setDataList(list);
      mergeFacultyOptions(list);
      setPagination({
        ...pagination,
        currentPage: currentPage ? currentPage : pagination.currentPage,
        rowCount: list.length,
      });

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onChangeCondition = (event) => {
    const { name, value } = event.target;
    setCondition({ ...condition, [name]: value });
  };

  const onChangeSelect = (name) => {
    return (value) => {
      setCondition({ ...condition, [name]: value });
    };
  };

  /**
   * NMU-529: clearing the filters has to re-run the search, not just empty the
   * inputs — otherwise the table keeps showing the previous result set while the
   * form claims no filter is applied. Back to page 1, since the record the user
   * was looking at on page 3 of a filtered list is somewhere else entirely in
   * the full one.
   */
  const onClearSearch = () => {
    const cleared = { ...defaultCondition };
    setCondition(cleared);
    doSearch(1, { condition: cleared });
  };

  const onChangePage = (currentPage) => {
    setPagination({ ...pagination, currentPage });
  };

  /**
   * NMU-453: drop focus before the dialog opens.
   *
   * antd puts aria-hidden on everything behind an open Modal. If the control
   * that opened it still holds focus, Chrome refuses — "Blocked aria-hidden on
   * an element because its descendant retained focus" — because hiding a
   * focused element from assistive tech strands screen-reader users on a node
   * that is supposed to be inert. Releasing focus first lets the Modal move it
   * inside the dialog, which is where it belongs anyway.
   */
  const releaseTriggerFocus = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const onAdd = () => {
    releaseTriggerFocus();
    setEditData(null);
    setIsShowAddFileDialog(true);
  };

  const onClickEdit = async (id) => {
    releaseTriggerFocus();
    try {
      dispatch(appLoaderAction.enable());
      const bean = await programService.getById(id);
      // NMU-453: the dialog only opens once there is something to show. The
      // global loader covers the fetch, so a slow response cannot leave a blank
      // form on screen that would save empty values over a real record.
      if (!bean) {
        throw "ไม่พบข้อมูลหลักสูตรนี้ | Programme not found";
      }
      setEditData(bean);
      setIsShowAddFileDialog(true);
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  const onSaveComplete = () => {
    setEditData(null);
    setIsShowAddFileDialog(false);
    doSearch(pagination.currentPage);
  };

  const onCancel = () => {
    setIsShowAddFileDialog(false);
    setEditData(null);
  };

  const onConfirmDelete = (id) => {
    onDelete(id);
  };

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await programService.remove(id);
      dispatch(appLoaderAction.disale());
      notifyUtitls.deleted("สำเร็จ", "ลบข้อมูลสำเร็จ");
      doSearch(1);
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  /*
    NMU-580: the วันที่/เวลาสร้าง column and the sort behind it.

    Programs is the one table that already holds every row — it fetches the
    whole list and cuts pages out of it below — so the sort is a comparator
    here rather than a re-query, applied before that slice. Letting antd sort
    would only reorder the ten rows it was handed.
  */
  const { createdColumn, onChangeTable, sortRows } = useClientCreatedSort();

  const columns = [
    {
      key: "title",
      align: "left",
      title: "ชื่อหลักสูตร",
      dataIndex: "title",
      render: (text) => <span>{text?.titleTH}</span>,
    },
    {
      key: "degreeLevel",
      title: "ระดับการศึกษา",
      dataIndex: "degreeLevel",
      render: (degreeLevel) => <span>{renderDegreeLevelLabel(degreeLevel)}</span>,
    },
    {
      key: "faculty",
      title: "คณะ",
      render: ({ faculty, facultyName }) => (
        <span>{facultyName?.titleTH ? facultyName.titleTH : faculty}</span>
      ),
    },
    {
      key: "majorList",
      title: "จำนวนสาขาวิชา",
      dataIndex: "majorList",
      render: (majorList) => <span>{majorList ? majorList.length : 0}</span>,
    },
    {
      title: "จำนวนผู้เข้าชม",
      dataIndex: "totalView",
      key: "totalView",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status ? (
          <Badge color="#4A9A0F" className="text-nowrap" text="Online" />
        ) : (
          <Badge color="#C0C0C0" className="text-nowrap" text="Offline" />
        ),
    },
    {
      title: "ดำเนินการ",
      key: "action",
      render: ({ programId }) => (
        <Space size="middle">
          <label
            style={{ cursor: "pointer" }}
            onClick={() => onClickEdit(programId)}
            className="pe-3 border-end"
          >
            <Icon.Edit size={18} />
          </label>

          <Popconfirm
            title="ยืนยันการลบข้อมูล ?"
            onConfirm={() => onConfirmDelete(programId)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <label style={{ cursor: "pointer" }}>
              <Icon.Trash size={18} />
            </label>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // NMU-580: ordered first, then cut — the other way round would sort a page.
  const pagedData = sortRows(dataList).slice(
    (pagination.currentPage - 1) * pagination.rowPerPage,
    pagination.currentPage * pagination.rowPerPage
  );

  return (
    <div>
      <h2 className="color-primary-title">หลักสูตร</h2>
      <Breadcrumb>
        <Breadcrumb.Item>การจัดการเนื้อหา</Breadcrumb.Item>
        <Breadcrumb.Item>หลักสูตร</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <div className="d-flex justify-content-between">
        <Button type="primary" onClick={() => onAdd()}>
          <Icon.Plus className="me-1" size={18} /> เพิ่มหลักสูตร
        </Button>
      </div>

      <Card className="mt-4" bordered={false}>
        <Collapse className="mb-4" defaultActiveKey={["1"]}>
          <Panel header="ตัวกรอง" key="1" className="search-panel">
            <Form layout="vertical">
              <Row gutter={20}>
                <Col xs={24} sm={8}>
                  <Form.Item label="ค้นหา (ชื่อหลักสูตร / คีย์เวิร์ด)" className="mb-0">
                    <Input
                      name="q"
                      value={condition.q}
                      onChange={onChangeCondition}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item label="ระดับการศึกษา" className="mb-0">
                    <Select
                      allowClear
                      value={condition.level}
                      onChange={onChangeSelect("level")}
                      placeholder="ทั้งหมด"
                    >
                      {DEGREE_LEVEL_OPTIONS.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item label="คณะ" className="mb-0">
                    <Select
                      allowClear
                      showSearch
                      value={condition.faculty}
                      onChange={onChangeSelect("faculty")}
                      placeholder="ทั้งหมด"
                      optionFilterProp="children"
                    >
                      {facultyOptions.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col className="mt-4">
                  <Button
                    type="primary"
                    className="w-100"
                    onClick={() => doSearch(1)}
                  >
                    <Icon.Search className="me-1" size={18} /> ค้นหา
                  </Button>
                </Col>
                <Col className="mt-4">
                  <Button className="btn-gray" onClick={() => onClearSearch()}>
                    ล้างค่า
                  </Button>
                </Col>
              </Row>
            </Form>
          </Panel>
        </Collapse>

        <CustomTable
          rowKey="programId"
          tableName="programTable"
          columns={withCreatedColumn(columns, createdColumn)}
          onChangeTable={onChangeTable}
          data={pagedData}
          scroll={{ x: true }}
          isPagination={true}
          onChangePage={onChangePage}
          currentPage={pagination.currentPage}
          pageSize={pagination.rowPerPage}
          totalRecord={pagination.rowCount}
        />
      </Card>

      <AddEditProgramDialog
        isShow={isShowAddFileDialog}
        editData={editData}
        facultyOptions={facultyOptions}
        onCancel={onCancel}
        onSaveComplete={onSaveComplete}
      />
    </div>
  );
};

export default Program;
